import React from 'react';
import { ProgressProvider, useProgress } from '@/contexts/ProgressContext';
import { MicroDashboardHero } from '@/components/micro/MicroDashboardHero';
import { MicroSessionCard } from '@/components/micro/MicroSessionCard';
import { MicroStatsPanel } from '@/components/micro/MicroStatsPanel';
import { ProfileCompletionBanner } from '@/components/profile/ProfileCompletionBanner';
import { useMicroPlaylist } from '@/hooks/useMicroPlaylist';
import { useMicroAIContent } from '@/hooks/useMicroAIContent';
import { usePersonalizedPath } from '@/hooks/usePersonalizedPath';
import { createPath } from '@/services/userPathService';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import PersonalizedCatalog from '@/components/onboarding/PersonalizedCatalog';
import { Link, useNavigate } from 'react-router-dom';
import { microSessionService } from '@/services/microSessionService';
import { gamificationService } from '@/services/gamificationService';
import { useInteractionTracking } from '@/hooks/useInteractionTracking';
import { MicroSession } from '@/data/microSessions';
import { microSessionCatalog } from '@/data/microSessions';
import { Loader2, ArrowRight } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { microToneFramework } from '@/content/microToneFramework';
import { getChallengeOption } from '@/data/challengeOptions';
import { microMotivations } from '@/content/microMotivations';

const MicroDashboardContent: React.FC = () => {
  // All hooks must be called before any conditional returns
  const { path, loading: pathLoading, refetch } = usePersonalizedPath();
  const { user, signOut } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const { trackCardClick } = useInteractionTracking('MicroDashboard');
  const playlistState = useMicroPlaylist();
  const { progressData } = useProgress();
  const { content: aiContent } = useMicroAIContent();
  const [skillFilter, setSkillFilter] = useState<'all' | 'Foundations' | 'Communication' | 'Operations' | 'Automation' | 'Leadership' | 'Insight' | 'AI Foundations'>('all');
  const [visibleCount, setVisibleCount] = useState(6);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);
  const [showPathSnapshot, setShowPathSnapshot] = useState(true);
  const [showCelebrate, setShowCelebrate] = useState(false);
  const [gamificationStats, setGamificationStats] = useState({
    level: 1,
    xp: 0,
    nextLevelXp: 100,
    streak: 0,
    levelTitle: 'Newcomer',
    totalTimeSpent: 0,
    levelProgression: [] as Array<{ level: number; title: string; xpRequired: number; cumulativeXp: number }>
  });
  const prevCompletedRef = React.useRef<number>(playlistState.stats.completed);
  const motivation = useMemo(() => {
    const index = Math.floor(Math.random() * microMotivations.length);
    return microMotivations[index];
  }, []);
  // Derived path sequence lives before any returns to keep hook order stable
  const pathSequence = useMemo(() => (path ? path.path : []), [path]);

  // Find the FIRST incomplete lesson in the path (not just current_index)
  // This ensures "Start next" goes to the right lesson even after skip-ahead
  const nextPathLessonId = useMemo(() => {
    // Find first lesson that is not completed
    const firstIncomplete = pathSequence.find(lessonId => {
      const progress = playlistState.progressMap[lessonId];
      return progress !== 'completed';
    });

    return firstIncomplete;
  }, [pathSequence, playlistState.progressMap]);

  const nextPathLesson = nextPathLessonId ? microSessionService.getSessionById(nextPathLessonId) : null;
  const heroSession = nextPathLesson || playlistState.dailyFocus;
  const challengeOption = path ? getChallengeOption(path.challenge) : null;

  // Initialize filter based on path focus
  useEffect(() => {
    playlistState.refresh();
  }, []);

  // Load gamification stats from context's progressData (properly loaded from Supabase)
  useEffect(() => {
    const levelProgression = gamificationService.getLevelProgression();

    // Use progressData from context (properly loaded via ProgressContext.refreshProgress)
    if (progressData) {
      // Calculate time based on completed sessions if totalTimeSpent is 0
      // Each micro-lesson averages about 10 minutes
      const estimatedTime = progressData.totalTimeSpent > 0
        ? progressData.totalTimeSpent
        : playlistState.stats.completed * 10;

      setGamificationStats({
        level: progressData.level,
        xp: progressData.experience,
        nextLevelXp: progressData.nextLevelExperience,
        streak: progressData.currentStreak,
        levelTitle: gamificationService.getLevelTitle(progressData.level),
        totalTimeSpent: estimatedTime,
        levelProgression
      });
    } else {
      // Use defaults if no progress data yet (loading in progress)
      setGamificationStats({
        level: 1,
        xp: 0,
        nextLevelXp: 100,
        streak: 0,
        levelTitle: 'Newcomer',
        totalTimeSpent: 0,
        levelProgression
      });
    }
  }, [progressData, playlistState.stats.completed]);

  useEffect(() => {
    if (path?.focus) {
      const focusMap: Record<string, typeof skillFilter> = {
        'Communication/Fundraising': 'Communication',
        'Programs/Projects': 'Operations',
        'Efficiency/Systems': 'Automation',
        'Assessment': 'AI Foundations'
      };
      const mappedFilter = focusMap[path.focus];
      if (mappedFilter) {
        setSkillFilter(mappedFilter);
      }
    }
  }, [path]);

  const skillTracks: Record<string, { title: string; description: string; focus: string; benefit: string }> = {
    Foundations: {
      title: 'Foundations',
      description: 'Lay down confident AI habits with Lyra before stacking complexity.',
      focus: 'Daily Pulse · Focus Reset',
      benefit: 'So you can open the day with one calm win and keep your streak alive.'
    },
    'AI Foundations': {
      title: 'AI Foundations',
      description: 'Master the fundamentals of working with AI effectively and responsibly.',
      focus: 'Core Principles · RAISE Framework · Ethics',
      benefit: 'So you can explain AI choices to stakeholders and choose the right tool with confidence.'
    },
    Communication: {
      title: 'Communication',
      description: 'Tighten prompts and repurpose updates so every channel sounds intentional.',
      focus: 'Prompt Sprint · Newsletter Remix',
      benefit: 'So you can ship copy that still sounds like you across email, SMS, and social.'
    },
    Operations: {
      title: 'Operations',
      description: 'Move work faster with structured triage and steady playbooks.',
      focus: 'Inbox Triage · Ops Playbook Builder',
      benefit: 'So you can calm the chaos, delegate cleanly, and guard focus time.'
    },
    Automation: {
      title: 'Automation',
      description: 'Find the loops that deserve a lightweight AI or Zapier handoff.',
      focus: 'Automation Spotlight',
      benefit: 'So you can hand repetitive work to bots without losing quality.'
    },
    Leadership: {
      title: 'Leadership',
      description: 'Scan the AI noise, choose the moves that fit your org readiness.',
      focus: 'Signal Scan · Momentum Brief',
      benefit: 'So you can brief your board and team on what matters (and shelve the hype).'
    },
    Insight: {
      title: 'Insight',
      description: 'Turn research notes and reflections into crisp briefs that travel.',
      focus: 'Research Brief Lab · Impact Reflection',
      benefit: 'So you can spot patterns faster and circulate a clear point of view.'
    }
  };

  const filteredSessions = useMemo(() => {
    if (skillFilter === 'all') return microSessionCatalog;
    return microSessionCatalog.filter((session) => session.skillArea === skillFilter);
  }, [skillFilter]);

  // Group sessions by topic when skill filter is active (visual grouping per Gabi's request)
  const sessionsByTopic = useMemo(() => {
    if (skillFilter === 'all') {
      return { ungrouped: filteredSessions.slice(0, visibleCount) };
    }

    // Group by topic for visual organization
    const grouped = filteredSessions.reduce((acc, session) => {
      const topicKey = session.topicId || 'other';
      if (!acc[topicKey]) {
        acc[topicKey] = {
          topicTitle: session.topicTitle,
          topicDescription: session.topicDescription,
          sessions: []
        };
      }
      acc[topicKey].sessions.push(session);
      return acc;
    }, {} as Record<string, { topicTitle?: string; topicDescription?: string; sessions: MicroSession[] }>);

    return grouped;
  }, [filteredSessions, skillFilter, visibleCount]);

  const visibleSessions = useMemo(() => {
    if (skillFilter === 'all') {
      return filteredSessions.slice(0, visibleCount);
    }
    return filteredSessions;
  }, [filteredSessions, visibleCount, skillFilter]);

  const canLoadMore = skillFilter === 'all' && visibleCount < filteredSessions.length;
  const activeTrackLabel = skillFilter === 'all' ? 'All tracks' : skillFilter;

  const mapStatus = (status?: string): 'not_started' | 'in_progress' | 'completed' => {
    if (status === 'completed') return 'completed';
    if (status === 'in_progress' || status === 'paused') return 'in_progress';
    return 'not_started';
  };

  const continueSessions = useMemo(() => {
    if (!heroSession) return playlistState.continueLearning;
    return playlistState.continueLearning.filter((session) => session.id !== heroSession.id);
  }, [playlistState.continueLearning, heroSession?.id]);

  const heroStatus = heroSession ? mapStatus(playlistState.progressMap[heroSession.id]) : 'not_started';
  const heroActionLabel = heroStatus === 'in_progress' ? 'Resume' : 'Start next';
  const heroActionTitle = heroSession?.displayTitle || heroSession?.title || 'Start a micro';

  useEffect(() => {
    if (skillFilter === 'all') {
      setVisibleCount(6);
    }
  }, [skillFilter]);

  // Handler function
  const handleStart = (session: MicroSession) => {
    microSessionService.startSession(session.id);
    trackCardClick(
      `micro-session-${session.id}`,
      session.title,
      session.route
    );
    navigate(session.route);
  };

  // Check for temp onboarding data on mount
  useEffect(() => {
    const checkTempOnboarding = async () => {
      const temp = localStorage.getItem('lyra-onboarding-temp');
      if (temp && user && !path && !pathLoading) {
        try {
          const selections = JSON.parse(temp);
          console.log("Found temp onboarding data, creating path...", selections);
          await createPath(selections.focus, selections.challenge, {
            userId: user.id,
            style: selections.style
          });
          localStorage.removeItem('lyra-onboarding-temp');
          await refetch();
          setShowWelcome(true);
        } catch (e) {
          console.error("Error processing temp onboarding data", e);
        }
      }
    };
    checkTempOnboarding();
  }, [user, path, pathLoading, refetch]);

  // Auto-collapse plan snapshot and show a celebratory nudge after the first logged micro
  useEffect(() => {
    if (prevCompletedRef.current === 0 && playlistState.stats.completed > 0) {
      setShowPathSnapshot(false);
      setShowCelebrate(true);
    }
    prevCompletedRef.current = playlistState.stats.completed;
  }, [playlistState.stats.completed]);

  // REMOVED: Profile completion gate - users can access content immediately
  // Profile completion is now optional and prompted via banner instead of blocking
  // Old code (removed for minimal viable onboarding):
  // useEffect(() => {
  //   if (!profileLoading && profile && !profile.profile_completed) {
  //     navigate('/complete-profile');
  //   }
  // }, [profileLoading, profile, navigate]);

  // Early returns after all hooks
  if (pathLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-slate-200 rounded-full mb-4"></div>
          <div className="h-4 w-32 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (playlistState.loading && !playlistState.dailyFocus) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const currentStepWhy = challengeOption?.learningPath.find((step) => step.id === heroSession?.id)?.why;
  const whyThisNext =
    currentStepWhy ||
    challengeOption?.problem ||
    microToneFramework.starterPhrases.guidanceHeader;
  const toneString = profile?.tone_glossary && profile.tone_glossary.length > 0 ? profile.tone_glossary.join(', ') : '';
  const audienceLabel = profile?.key_audience ? profile.key_audience.replace(/_/g, ' ') : '';
  const channelLabel = profile?.primary_channel ? profile.primary_channel.replace(/_/g, ' ') : '';
  const personalizedHeadline = heroSession
    ? `A ${heroSession.estimatedMinutes}m win for ${audienceLabel || 'your audience'}${channelLabel ? ` on ${channelLabel}` : ''}`.replace('  ', ' ').trim()
    : microToneFramework.starterPhrases.heroTagline;
  const personalizedTip = toneString
    ? `Use your tone cues: ${toneString}.`
    : microToneFramework.starterPhrases.guidanceHeader;

  return (
    <>
      <div className="min-h-screen bg-slate-50 px-4 pt-12 pb-32 text-slate-900 md:pb-12">
        <div className="max-w-6xl mx-auto space-y-10">
          {showWelcome && path && (
            <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-lg flex items-start justify-between gap-4 animate-fade-in">
              <div>
                <h2 className="font-semibold text-lg">Welcome to your personalized path</h2>
                <p className="text-slate-300 mt-1">
                  We've set up your <strong>{path.focus}</strong> journey. Start with your first tiny win below.
                </p>
              </div>
              <button
                onClick={() => setShowWelcome(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                Dismiss
              </button>
            </div>
          )}
          {!path && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-600">Path setup</p>
                <h2 className="text-lg font-semibold text-amber-900">Pick a quick path to personalize your dashboard.</h2>
                <p className="text-sm text-amber-800 mt-1">
                  We could not find your saved path yet. You can start the daily lesson below or set up your path now.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/onboarding')}
                  className="rounded-full bg-amber-900 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-800 transition"
                >
                  Build my path
                </button>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="rounded-full border border-amber-300 px-4 py-2 text-xs uppercase tracking-[0.2em] text-amber-900 hover:border-amber-500"
                >
                  Try again
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end text-sm text-slate-600">
            <nav className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
              <Link className="hover:text-slate-900 underline-offset-4 hover:underline" to="/profile">
                Profile
              </Link>
              <button
                type="button"
                onClick={signOut}
                className="rounded-full border border-slate-200 px-3 py-1 hover:border-slate-400 hover:text-slate-900 transition"
              >
                Log out
              </button>
            </nav>
          </div>

          <MicroDashboardHero
            session={heroSession}
            motivation={motivation}
            stats={{
              headline: personalizedHeadline || aiContent.headline,
              tip: personalizedTip || aiContent.tip
            }}
          />

          {/* XP & Gamification Stats Panel */}
          <MicroStatsPanel
            level={gamificationStats.level}
            xp={gamificationStats.xp}
            nextLevelXp={gamificationStats.nextLevelXp}
            streak={gamificationStats.streak}
            completed={playlistState.stats.completed}
            inProgress={playlistState.stats.inProgress}
            levelTitle={gamificationStats.levelTitle}
            levelProgression={gamificationStats.levelProgression}
          />

          {heroSession && (
            <section className="space-y-4">
              <div className="flex items-center justify-between text-slate-500 text-xs uppercase tracking-[0.3em]">
                <span>{heroStatus === 'in_progress' ? 'Resume' : 'Today'}</span>
                <span>{heroStatus === 'in_progress' ? 'Pick up your streak' : 'Your next tiny win'}</span>
              </div>
              <MicroSessionCard
                session={heroSession}
                onStart={handleStart}
                status={heroStatus}
                overline={heroStatus === 'in_progress' ? 'Resume' : 'Today'}
                recommended={heroStatus !== 'completed'}
                userId={user?.id}
              />
            </section>
          )}

          {continueSessions.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between text-slate-500 text-xs uppercase tracking-[0.3em]">
                <span>Continue</span>
                <span>Pick up where you left off</span>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 md:grid md:grid-cols-2 md:mx-0 md:px-0 md:overflow-visible">
                {continueSessions.map((session) => (
                  <div key={session.id} className="min-w-[260px] md:min-w-0">
                    <MicroSessionCard
                      session={session}
                      onStart={handleStart}
                      status={mapStatus(playlistState.progressMap[session.id])}
                      userId={user?.id}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Non-blocking profile completion prompt */}
          <ProfileCompletionBanner />

          {showCelebrate && heroSession && (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-700">Nice first win</p>
                <h3 className="text-lg font-semibold text-emerald-900">Keep the streak alive with your next tiny win.</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleStart(heroSession)}
                  className="rounded-full bg-emerald-700 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-800 transition"
                >
                  Start next
                </button>
                <button
                  type="button"
                  onClick={() => setShowCelebrate(false)}
                  className="rounded-full border border-emerald-300 px-3 py-2 text-xs uppercase tracking-[0.2em] text-emerald-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Plan snapshot to reduce overload and keep a single primary action */}




          {path && (
            <PersonalizedCatalog
              expanded={showPathSnapshot}
              onToggle={() => setShowPathSnapshot((prev) => !prev)}
            />
          )}

          <div className="flex items-center justify-between rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Lesson Hub</p>
              <p className="text-sm text-slate-600">Browse all tracks and lessons.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowBrowse((prev) => !prev)}
              className="rounded-full border border-slate-300 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-700 hover:border-slate-500"
            >
              {showBrowse ? 'Hide catalog' : 'Browse all'}
            </button>
          </div>

          {showBrowse && (
            <section className="rounded-[32px] border border-slate-200 bg-white p-6 space-y-6 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Quick access hub</p>
                  <h2 className="text-lg font-semibold text-slate-900">Browse by track</h2>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {['all', 'Foundations', 'AI Foundations', 'Communication', 'Operations', 'Automation', 'Leadership', 'Insight'].map((filter) => {
                  const isActive = skillFilter === filter;
                  return (
                    <button
                      key={filter}
                      onClick={() => setSkillFilter(filter as any)}
                      aria-pressed={isActive}
                      className={`px-4 py-2 rounded-full border text-xs uppercase tracking-[0.2em] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 ${isActive
                        ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                        : 'bg-slate-100 text-slate-900 border-slate-200 hover:border-slate-400 hover:bg-slate-200/80'
                        }`}
                    >
                      {filter === 'all' ? 'All tracks' : filter}
                    </button>
                  );
                })}
              </div>
              {skillFilter !== 'all' && skillTracks[skillFilter] && (
                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5 space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Now exploring</p>
                  <h3 className="text-lg font-semibold text-slate-900">{skillTracks[skillFilter].title}</h3>
                  <p className="text-sm text-slate-600">{skillTracks[skillFilter].description}</p>
                  <p className="text-sm text-slate-600">{skillTracks[skillFilter].benefit}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                    Focus: {skillTracks[skillFilter].focus}
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-500">
                <span>Viewing: {activeTrackLabel}</span>
                <span>
                  {visibleSessions.length}
                  {skillFilter === 'all' ? ` of ${filteredSessions.length}` : ''} sessions
                </span>
              </div>
              {visibleSessions.length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-slate-200 p-6 text-sm text-slate-500">
                  No sessions in this track yet.
                </div>
              ) : skillFilter === 'all' ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {visibleSessions.map((session) => (
                    <MicroSessionCard
                      key={`hub-${session.id}`}
                      session={session}
                      onStart={handleStart}
                      status={mapStatus(playlistState.progressMap[session.id])}
                      userId={user?.id}
                    />
                  ))}
                </div>
              ) : (
                // Visual grouping by topic when skill filter is active
                <div className="space-y-8">
                  {Object.entries(sessionsByTopic).map(([topicId, topicData]) => (
                    <div key={topicId}>
                      {topicData.topicTitle && topicId !== 'other' && (
                        <div className="mb-4">
                          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-700 font-semibold">
                            Topic: {topicData.topicTitle}
                          </p>
                          {topicData.topicDescription && (
                            <p className="text-sm text-slate-600 mt-1">
                              {topicData.topicDescription}
                            </p>
                          )}
                          <p className="text-xs text-slate-600 mt-1">
                            {topicData.sessions.length} {topicData.sessions.length === 1 ? 'lesson' : 'lessons'}
                          </p>
                        </div>
                      )}
                      <div className="grid gap-4 md:grid-cols-2">
                        {topicData.sessions.map((session) => (
                          <MicroSessionCard
                            key={`topic-${session.id}`}
                            session={session}
                            onStart={handleStart}
                            status={mapStatus(playlistState.progressMap[session.id])}
                            userId={user?.id}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {canLoadMore && (
                <div className="flex justify-end">
                  <button
                    onClick={() => setVisibleCount((count) => count + 6)}
                    className="rounded-full border border-slate-200 px-6 py-2 text-xs uppercase tracking-[0.3em] text-slate-600 hover:border-slate-400"
                  >
                    Show more
                  </button>
                </div>
              )}
            </section>
          )}

        </div>
      </div>
      {heroSession && (
        <div className="fixed inset-x-0 bottom-0 z-40 md:hidden">
          <div className="mx-auto max-w-6xl px-4 pb-[env(safe-area-inset-bottom)]">
            <div className="rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
              <button
                type="button"
                onClick={() => handleStart(heroSession)}
                className="flex w-full items-center justify-between gap-3 rounded-xl bg-slate-900 px-4 py-3 text-left text-white"
              >
                <span className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300">{heroActionLabel}</span>
                  <span className="text-base font-semibold">{heroActionTitle}</span>
                </span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MicroDashboardPage: React.FC = () => {
  return (
    <ProgressProvider>
      <MicroDashboardContent />
    </ProgressProvider>
  );
};

export default MicroDashboardPage;
